import { useState } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import type { AdminStore } from '@/client/types/stores';
import {
  useStorePayoutConfigQuery,
  useStorePayoutsQuery,
} from '@/client/api/stores/stores.query';
import { useSetPayoutHold } from '@/client/api/stores/stores.mutation';
import { useStorePayoutBalanceQuery } from '@/client/api/stores/parity.query';
import { useUpdateStorePayoutConfig } from '@/client/api/stores/parity.mutation';
import type { StorePayoutConfigUpdate } from '@/client/types/stores/parity';
import type { StorePayoutConfig } from '@/client/types/stores';
import { useGate } from '@/client/hooks/useGate';
import { Input } from '@/components/ui/input';
import { formatMoney } from '@/client/types/payment';
import { FieldRow, SaveRow, Toggle } from './settings/controls';
import { PERMISSIONS } from '@/client/config/permissions';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { cn } from '@/lib/utils';
import { InfoRow, SectionCard, money, TableSkeleton, CardSkeleton, InfoRowsSkeleton } from './shared';
import { ReasonDialog } from './ReasonDialog';


const SCHEDULES = ['daily', 'weekly', 'biweekly', 'monthly', 'manual'] as const;

/**
 * Payout schedule, auto-payout and minimum. Where the money goes (bank / MoMo /
 * Paystack recipient) is seller-only — ff-api refuses those fields from the
 * console (`destination_locked`).
 */
const PayoutSettingsForm: React.FC<{ storeid: number; config: StorePayoutConfig | null }> = ({ storeid, config }) => {
  const initial = {
    schedule: (config?.schedule ?? 'biweekly') as StorePayoutConfigUpdate['schedule'],
    auto_payout_enabled: Boolean(config?.auto_payout_enabled ?? 1),
    min_payout_amount: String(config?.min_payout_amount ?? 50),
  };
  const [form, setForm] = useState(initial);
  const [confirm, setConfirm] = useState(false);
  const save = useUpdateStorePayoutConfig();
  const canEdit = useGate(PERMISSIONS.store.payout).allowed;

  const changes: StorePayoutConfigUpdate = {};
  if (form.schedule !== initial.schedule) changes.schedule = form.schedule;
  if (form.auto_payout_enabled !== initial.auto_payout_enabled) changes.auto_payout_enabled = form.auto_payout_enabled;
  if (form.min_payout_amount !== initial.min_payout_amount) changes.min_payout_amount = Number(form.min_payout_amount);
  const valid = Number(form.min_payout_amount) >= 0 && form.min_payout_amount.trim() !== '';

  return (
    <SectionCard title="Payout settings" description="Bank and mobile-money details can only be changed by the seller.">
      <div className="divide-y divide-gray-100">
        <FieldRow label="Schedule">
          <select
            disabled={!canEdit}
            value={form.schedule}
            onChange={(e) => setForm((f) => ({ ...f, schedule: e.target.value as StorePayoutConfigUpdate['schedule'] }))}
            className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm capitalize"
          >
            {SCHEDULES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </FieldRow>
        <FieldRow label="Automatic payouts">
          <Toggle disabled={!canEdit} checked={form.auto_payout_enabled} onChange={(v) => setForm((f) => ({ ...f, auto_payout_enabled: v }))} />
        </FieldRow>
        <FieldRow label="Minimum payout" hint="GHS">
          <Input disabled={!canEdit} type="number" min={0} value={form.min_payout_amount} onChange={(e) => setForm((f) => ({ ...f, min_payout_amount: e.target.value }))} className="w-28" />
        </FieldRow>
      </div>
      <SaveRow dirty={Object.keys(changes).length > 0 && valid} saving={save.isPending} onSave={() => setConfirm(true)} onReset={() => setForm(initial)} />
      {confirm && (
        <ReasonDialog
          open
          title="Change payout settings"
          description={`Changes: ${Object.keys(changes).join(', ')}. The seller is notified.`}
          confirmLabel="Save"
          reasonRequired
          loading={save.isPending}
          onConfirm={(reason) => save.mutate({ storeid, reason, ...changes }, { onSuccess: () => setConfirm(false) })}
          onClose={() => setConfirm(false)}
        />
      )}
    </SectionCard>
  );
};

const STATUS_TONE: Record<string, string> = {
  completed: 'bg-green-100 text-green-800',
  processing: 'bg-blue-100 text-blue-800',
  pending: 'bg-amber-100 text-amber-800',
  failed: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-600',
};

export const PayoutTab: React.FC<{ store: AdminStore }> = ({ store }) => {
  const { data: config, isLoading: loadingConfig } = useStorePayoutConfigQuery(store.storeid);
  const { data: payouts = [], isLoading: loadingPayouts } = useStorePayoutsQuery(store.storeid);
  const { data: balance } = useStorePayoutBalanceQuery(store.storeid);
  const setHold = useSetPayoutHold();
  const [dialog, setDialog] = useState<null | 'hold' | 'release'>(null);

  if (loadingConfig)
    return (
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-5 md:col-span-2">
          <Skeleton className="h-3 w-32" />
          <Skeleton className="mt-2 h-8 w-40" />
          <Skeleton className="mt-2 h-3 w-72" />
        </div>
        <CardSkeleton />
        <CardSkeleton description><InfoRowsSkeleton rows={2} /></CardSkeleton>
      </div>
    );

  const held = Boolean(config?.payout_hold);

  const run = (reason: string) => {
    setHold.mutate(
      { storeid: store.storeid, hold: dialog === 'hold', reason },
      { onSuccess: () => setDialog(null) },
    );
  };

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="rounded-lg border border-gray-200 bg-white p-5 md:col-span-2">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Available to pay out</p>
        <p className="mt-1 text-3xl font-black text-gray-900">{balance ? formatMoney(balance.balance, balance.ccode) : '—'}</p>
        <p className="mt-1 text-xs text-gray-500">
          Wallet balance as of now. The console reads it without reconciling unpaid order credits.
        </p>
      </div>

      <SectionCard title="Payout destination">
        {config ? (
          <div className="divide-y divide-gray-100">
            <InfoRow label="Provider"><span className="uppercase">{config.provider ?? '—'}</span></InfoRow>
            <InfoRow label="Bank">{config.bank_name ?? '—'}</InfoRow>
            <InfoRow label="Account">{config.account_number ?? '—'}{config.account_name ? ` · ${config.account_name}` : ''}</InfoRow>
            {config.momo_msisdn && (
              <InfoRow label="Mobile money">{config.momo_msisdn}{config.momo_network ? ` (${config.momo_network})` : ''}</InfoRow>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-400">No payout configuration set for this store.</p>
        )}
      </SectionCard>

      <PayoutSettingsForm key={`${config?.schedule}-${config?.auto_payout_enabled}-${config?.min_payout_amount}`} storeid={store.storeid} config={config ?? null} />

      <SectionCard
        title="Payout hold"
        actions={
          <span className={cn('rounded-full px-2 py-1 text-xs font-semibold', held ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800')}>
            {held ? 'On hold' : 'Released'}
          </span>
        }
      >
        {held && <p className="mb-3 text-sm text-gray-600">Reason: {config?.payout_hold_reason ?? '—'}</p>}
        {held ? (
          <PermissionButton code={PERMISSIONS.store.payout} variant="outline" onClick={() => setDialog('release')}>Release hold</PermissionButton>
        ) : (
          <PermissionButton code={PERMISSIONS.store.payout} variant="destructive" onClick={() => setDialog('hold')}>Hold payouts</PermissionButton>
        )}
      </SectionCard>

      <div className="md:col-span-2">
        <SectionCard title="Recent payouts">
          {loadingPayouts ? (
            <TableSkeleton rows={4} cols={5} />
          ) : payouts.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-400">No payouts yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-gray-400">
                    <th className="py-2 pr-4">Requested</th>
                    <th className="py-2 pr-4">Amount</th>
                    <th className="py-2 pr-4">Fee</th>
                    <th className="py-2 pr-4">Net</th>
                    <th className="py-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {payouts.map((p) => (
                    <tr key={p.poid}>
                      <td className="py-2 pr-4 text-gray-600">{p.date_requested ? new Date(p.date_requested).toLocaleDateString() : '—'}</td>
                      <td className="py-2 pr-4 tabular-nums">{money(p.amount)}</td>
                      <td className="py-2 pr-4 tabular-nums text-gray-500">{money(p.fee)}</td>
                      <td className="py-2 pr-4 tabular-nums font-medium">{money(p.net_amount)}</td>
                      <td className="py-2">
                        <span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold capitalize', STATUS_TONE[p.status ?? ''] ?? 'bg-gray-100 text-gray-600')}>
                          {p.status ?? '—'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      </div>

      {dialog && (
        <ReasonDialog
          open
          title={dialog === 'hold' ? 'Hold payouts' : 'Release payout hold'}
          description={dialog === 'hold' ? 'Freezes payouts for this store until released.' : 'Resumes payouts for this store.'}
          confirmLabel={dialog === 'hold' ? 'Hold' : 'Release'}
          destructive={dialog === 'hold'}
          reasonRequired={dialog === 'hold'}
          loading={setHold.isPending}
          onConfirm={run}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
};
